import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { complianceAPI, locationsAPI, branchesAPI, employeesAPI } from '../lib/api';
import { Link } from 'react-router-dom';
import {
  Shield,
  FileCheck,
  Syringe,
  GraduationCap,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  ArrowRight,
  Download,
  ChevronDown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useOrgLabels } from '../contexts/OrgLabelsContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function Compliance() {
  const orgL = useOrgLabels();
  const { isAdmin, user } = useAuth();
  const scopeType = user?.effective_permissions?.scope_type;
  const canExportCompany = isAdmin || scopeType === 'all';
  const canExportBranch = canExportCompany || scopeType === 'branch';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [locationFilter, setLocationFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [followUps, setFollowUps] = useState([]);

  // Listen-Export (Fuehrungszeugnisse / Masernschutz) — eigene Auswahl
  const [listScope, setListScope] = useState('location'); // 'company' | 'branch' | 'location'
  const [listScopeId, setListScopeId] = useState('');
  const [listLoading, setListLoading] = useState(null); // 'police' | 'measles' | null

  useEffect(() => {
    locationsAPI.getAll()
      .then((res) => setLocations(res.data || []))
      .catch(() => setLocations([]));
    if (canExportBranch) {
      branchesAPI.getAll()
        .then((res) => setBranches(res.data || []))
        .catch(() => setBranches([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default-Scope abhaengig vom Recht
  useEffect(() => {
    if (canExportCompany) setListScope('company');
    else if (canExportBranch) setListScope('branch');
    else setListScope('location');
  }, [canExportCompany, canExportBranch]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = locationFilter !== 'all' ? { location_id: locationFilter } : {};
      const response = await complianceAPI.getOverview(params);
      setData(response.data);
      // Folgetermine aus Gespraechsnotizen — best-effort, silent fail.
      try {
        const fuRes = await employeesAPI.listNoteFollowUps({ days_ahead: 30 });
        setFollowUps(fuRes.data || []);
      } catch {
        setFollowUps([]);
      }
    } catch (error) {
      console.error('Failed to load compliance data:', error);
      toast.error('Compliance-Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const params = { format };
      if (locationFilter !== 'all') params.location_id = locationFilter;
      const res = await complianceAPI.exportHandlungsbedarf(params);
      const ext = format === 'xlsx' ? 'xlsx' : 'csv';
      const mime = format === 'xlsx'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv;charset=utf-8';
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance_handlungsbedarf.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Export gestartet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  const handleListExport = async (kind) => {
    if (listScope !== 'company' && !listScopeId) {
      toast.error(listScope === 'branch' ? `Bitte ${orgL.branch} wählen` : `Bitte ${orgL.location} wählen`);
      return;
    }
    setListLoading(kind);
    try {
      const params = { scope: listScope };
      if (listScope !== 'company') params.id = listScopeId;
      const res = kind === 'police'
        ? await complianceAPI.exportPoliceChecks(params)
        : await complianceAPI.exportMeasles(params);

      // Dateiname aus Content-Disposition extrahieren (Backend liefert sauber benannten Filename)
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      const fallback = kind === 'police' ? 'Fuehrungszeugnisse.xlsx' : 'Masernschutz.xlsx';
      const fileName = match?.[1] || fallback;

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`${kind === 'police' ? 'Führungszeugnisse' : 'Masernschutz'}-Liste heruntergeladen`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export fehlgeschlagen');
    } finally {
      setListLoading(null);
    }
  };

  const getPercentage = (valid, total) => {
    if (total === 0) return 100;
    return Math.round((valid / total) * 100);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={`skel-${i}`} className="h-48 rounded-xl" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const policeCheckTotal = (data?.police_check?.valid || 0) + (data?.police_check?.expired || 0) + (data?.police_check?.missing || 0);
  const policeCheckPercent = getPercentage(data?.police_check?.valid || 0, policeCheckTotal);

  const measlesPercent = getPercentage(data?.measles_protection?.verified || 0, data?.measles_protection?.required || 0);

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="compliance-page">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground">Compliance Übersicht</h1>
            <p className="text-muted-foreground mt-1">
              Status von Führungszeugnissen, Masernschutz und Weiterbildungen
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-[220px]" data-testid="compliance-location-filter">
                <SelectValue placeholder={`${orgL.location} wählen`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle {orgL.locations}</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full"
                  disabled={exporting}
                  data-testid="compliance-export-trigger"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Handlungsbedarf exportieren
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="compliance-export-csv">
                  CSV (.csv)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('xlsx')} data-testid="compliance-export-xlsx">
                  Excel (.xlsx)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Führungszeugnis */}
          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileCheck className="h-5 w-5 text-primary" />
                Führungszeugnis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold font-heading">{policeCheckPercent}%</span>
                <Badge className={policeCheckPercent >= 80 ? 'badge-success' : 'badge-warning'}>
                  {data?.police_check?.valid || 0} / {policeCheckTotal} gültig
                </Badge>
              </div>
              <Progress value={policeCheckPercent} className="h-2" />
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div>
                  <p className="font-bold text-green-600">{data?.police_check?.valid || 0}</p>
                  <p className="text-muted-foreground">Gültig</p>
                </div>
                <div>
                  <p className="font-bold text-amber-600">{data?.police_check?.expired || 0}</p>
                  <p className="text-muted-foreground">Abgelaufen</p>
                </div>
                <div>
                  <p className="font-bold text-red-600">{data?.police_check?.missing || 0}</p>
                  <p className="text-muted-foreground">Fehlt</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Masernschutz */}
          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Syringe className="h-5 w-5 text-accent" />
                Masernschutz
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold font-heading">{measlesPercent}%</span>
                <Badge className={measlesPercent >= 80 ? 'badge-success' : 'badge-warning'}>
                  {data?.measles_protection?.verified || 0} / {data?.measles_protection?.required || 0} verifiziert
                </Badge>
              </div>
              <Progress value={measlesPercent} className="h-2" />
              <div className="grid grid-cols-2 gap-2 text-center text-sm">
                <div>
                  <p className="font-bold text-green-600">{data?.measles_protection?.verified || 0}</p>
                  <p className="text-muted-foreground">Verifiziert</p>
                </div>
                <div>
                  <p className="font-bold text-red-600">{data?.measles_protection?.missing || 0}</p>
                  <p className="text-muted-foreground">Ausstehend</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Pflicht für Mitarbeiter geboren nach 1970
              </p>
            </CardContent>
          </Card>

          {/* Weiterbildungen */}
          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-blue-600" />
                Weiterbildungen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold font-heading">{data?.expiring_trainings?.length || 0}</span>
                <Badge className={data?.expiring_trainings?.length === 0 ? 'badge-success' : 'badge-warning'}>
                  Ablaufend
                </Badge>
              </div>
              {data?.expiring_trainings?.length > 0 ? (
                <div className="space-y-2">
                  {data.expiring_trainings.slice(0, 3).map((training) => (
                    <div key={`${training.employee_name}-${training.training_title}`} className="text-sm flex items-center gap-2">
                      <Clock className="h-3 w-3 text-amber-600" />
                      <span className="truncate">{training.employee_name}: {training.training_title}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm">Keine ablaufenden Zertifikate</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Folgetermine aus Gespraechsnotizen */}
          <Card className="border-border/60" data-testid="compliance-followups-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" />
                Offene Folgetermine
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              {followUps.length === 0 ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm">Keine offenen Gesprächs-Folgetermine</span>
                </div>
              ) : (
                <div className="space-y-1">
                  {followUps.slice(0, 4).map((fu) => (
                    <div key={fu.id} className="text-sm flex items-center gap-2">
                      <Clock className="h-3 w-3 text-primary shrink-0" />
                      <span className="font-mono text-xs text-stone-500 shrink-0">
                        {fu.follow_up_date}
                      </span>
                      <span className="truncate">
                        <strong>{fu.employee_name}</strong>: {fu.title}
                      </span>
                    </div>
                  ))}
                  {followUps.length > 4 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      … und {followUps.length - 4} weitere
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Excel-Listen-Export: Fuehrungszeugnisse + Masernschutz */}
        <Card className="border-border/60" data-testid="compliance-list-export-card">
          <CardHeader className="border-b border-border/40">
            <CardTitle className="font-heading flex items-center gap-2">
              <Download className="h-5 w-5 text-purple-600" />
              Compliance-Listen als Excel exportieren
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              Lade die Fuehrungszeugnis- oder Masernschutz-Liste fuer einen Standort, eine Niederlassung
              oder die gesamte Firma als Excel-Datei herunter. Mitarbeiter ohne Eintrag erscheinen ebenfalls
              (mit leeren Datumsfeldern), inaktive Mitarbeiter werden ausgeblendet.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Bereich</label>
                <Select
                  value={listScope}
                  onValueChange={(v) => { setListScope(v); setListScopeId(''); }}
                >
                  <SelectTrigger data-testid="compliance-list-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {canExportCompany && <SelectItem value="company">Gesamte Firma</SelectItem>}
                    {canExportBranch && <SelectItem value="branch">{orgL.branch}</SelectItem>}
                    <SelectItem value="location">{orgL.location}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {listScope !== 'company' && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    {listScope === 'branch' ? orgL.branch : orgL.location}
                  </label>
                  <Select value={listScopeId} onValueChange={setListScopeId}>
                    <SelectTrigger data-testid="compliance-list-scope-id">
                      <SelectValue placeholder={listScope === 'branch' ? `${orgL.branch} wählen…` : `${orgL.location} wählen…`} />
                    </SelectTrigger>
                    <SelectContent>
                      {(listScope === 'branch' ? branches : locations).map((item) => (
                        <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => handleListExport('police')}
                disabled={listLoading !== null || (listScope !== 'company' && !listScopeId)}
                data-testid="compliance-export-police-btn"
                className="rounded-full"
              >
                <FileCheck className="mr-2 h-4 w-4 text-emerald-600" />
                {listLoading === 'police' ? 'Lädt…' : 'Führungszeugnisse (.xlsx)'}
              </Button>
              <Button
                variant="outline"
                onClick={() => handleListExport('measles')}
                disabled={listLoading !== null || (listScope !== 'company' && !listScopeId)}
                data-testid="compliance-export-measles-btn"
                className="rounded-full"
              >
                <Syringe className="mr-2 h-4 w-4 text-purple-600" />
                {listLoading === 'measles' ? 'Lädt…' : 'Masernschutz (.xlsx)'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Employees Needing Attention */}
        <Card className="border-border/60">
          <CardHeader className="border-b border-border/40">
            <CardTitle className="font-heading flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Handlungsbedarf
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {data?.employees_needing_attention?.length > 0 ? (
              <div className="divide-y divide-border/40">
                {data.employees_needing_attention.map((emp, index) => (
                  <div
                    key={emp.id}
                    className="p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors animate-slide-up"
                    style={{ animationDelay: `${index * 0.03}s` }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-amber-100">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium">{emp.name}</p>
                        <p className="text-sm text-muted-foreground font-mono">{emp.employee_number}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex flex-wrap gap-1">
                        {emp.issues.map((issue) => (
                          <Badge key={issue} variant="outline" className="text-xs text-amber-700 border-amber-300">
                            {issue}
                          </Badge>
                        ))}
                      </div>
                      <Link to={`/employees/${emp.id}`}>
                        <Button variant="ghost" size="sm">
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-heading font-semibold text-lg">Alles in Ordnung!</h3>
                <p className="text-muted-foreground mt-1">
                  Alle Mitarbeiter haben gültige Dokumente
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary */}
        <Card className="border-border/60 bg-secondary/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-muted-foreground" />
                <span className="text-muted-foreground">
                  Gesamt: <strong className="text-foreground">{data?.total_employees || 0}</strong> aktive Mitarbeiter
                </span>
              </div>
              <Link to="/employees">
                <Button variant="outline" size="sm" className="rounded-full">
                  Alle Mitarbeiter anzeigen
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
